/**
 * WorkflowEditorPage
 * Full-screen visual workflow editor — design replicates workflow-draft.html exactly.
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  listOrderStatusDefinitions,
  getOrderTypeStatuses,
  replaceOrderTypeStatuses,
  listOrderTypes,
} from '../app/api';

/* ─── Injected CSS (scoped via .wfe prefix) ─────────────────────────────── */
const CSS = `
.wfe-root *, .wfe-root *::before, .wfe-root *::after { box-sizing:border-box; margin:0; padding:0; }
.wfe-root { font-family:'Segoe UI',system-ui,sans-serif; background:#f0f2f5; color:#1a1a2e; height:100vh; overflow:hidden; display:flex; flex-direction:column; }
.wfe-root.wfe-root--inline { height:100%; min-height:0; }
.wfe-root .btn { display:inline-flex;align-items:center;gap:5px;padding:5px 13px;border-radius:8px;font-size:12px;font-weight:700;cursor:pointer;border:none;transition:all .15s;white-space:nowrap;flex-shrink:0; }
.wfe-root .btn-primary  { background:#7c8ff7;color:#fff; }
.wfe-root .btn-primary:hover { background:#5c6fd4; }
.wfe-root .btn-ghost    { background:transparent;color:#b0b8ff;border:1.5px solid #4a4f6e; }
.wfe-root .btn-ghost:hover  { border-color:#7c8ff7;color:#fff; }
.wfe-root .btn-danger   { background:#e53e3e;color:#fff; }
.wfe-root .btn-layout   { background:#ffffffcc;color:#3d3d5c;border:1.5px solid #e4e6ef;backdrop-filter:blur(4px); }

/* layout */
.wfe-layout { display:flex; flex:1; overflow:hidden; }

/* sidebar */
.wfe-sidebar { width:230px;min-width:190px;background:#fff;border-right:1px solid #e4e6ef;display:flex;flex-direction:column;flex-shrink:0; }
.wfe-sidebar-legend { flex-shrink:0;padding:10px 10px 8px;border-bottom:1px solid #e4e6ef;background:#fafbff; }
.wfe-sidebar-legend h4 { font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:#9fa8da;margin-bottom:7px; }
.wfe-sidebar-legend .wfe-lrow { font-size:10px;gap:6px;margin-bottom:5px; }
.wfe-sidebar-legend .wfe-lrow:last-child { margin-bottom:0; }
.wfe-sidebar-head { padding:12px 12px 8px;border-bottom:1px solid #e4e6ef; }
.wfe-sidebar-head h2 { font-size:11px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;color:#7a7f9a; }
.wfe-sidebar-hint { margin-top:5px;font-size:11px;color:#aab0cc;line-height:1.4; }
.wfe-sidebar-list { flex:1;overflow-y:auto;padding:7px; }
.wfe-chip { display:flex;align-items:center;gap:7px;padding:7px 9px;border-radius:9px;margin-bottom:4px;font-size:12px;font-weight:600;border:1.5px solid #e8eaf6;background:#fff;transition:box-shadow .12s,border-color .12s;cursor:grab;user-select:none; }
.wfe-chip:hover { box-shadow:0 2px 8px #0002;border-color:#9fa8da; }
.wfe-chip.in-flow { background:#f3f4ff;border-color:#c5cae9;cursor:default;opacity:.6; }
.wfe-chip .dot { width:11px;height:11px;border-radius:50%;flex-shrink:0; }
.wfe-chip .lbl { flex:1; }
.wfe-chip .badge { font-size:9px;font-weight:800;padding:2px 5px;border-radius:7px;background:#e8eaf6;color:#7986cb; }
.wfe-chip.in-flow .badge { background:#c8e6c9;color:#388e3c; }
.wfe-drag-icon { font-size:13px;opacity:.3;flex-shrink:0; }

/* canvas */
.wfe-canvas-wrap { flex:1;overflow:auto;position:relative;background:#f7f8fc;background-image:radial-gradient(circle,#c5cae9 1.2px,transparent 1.2px);background-size:26px 26px; }
.wfe-canvas-wrap.drag-over { background-color:#eef0ff; }
.wfe-canvas-wrap.pan-mid { cursor:grabbing; }
#wfe-canvas { position:relative;min-width:1200px;min-height:700px;transform-origin:0 0; }
#wfe-svg-layer { position:absolute;top:0;left:0;width:100%;height:100%;overflow:visible;z-index:5; }

/* node */
.wfe-node { position:absolute;width:172px;background:#fff;border-radius:13px;border:2px solid #e4e6ef;box-shadow:0 2px 10px #0002;user-select:none;z-index:10;transition:border-color .15s,box-shadow .15s; }
.wfe-node:hover { box-shadow:0 4px 18px #0003;border-color:#9fa8da; }
.wfe-node.is-final   { border-style:dashed;border-color:#66bb6a !important; }
.wfe-node.is-initial { border-color:#7c8ff7 !important;box-shadow:0 0 0 3px #7c8ff720 !important; }
.wfe-node-top { display:flex;align-items:center;gap:7px;padding:10px 11px 8px;border-bottom:1px solid #f0f0f0;cursor:move; }
.wfe-node-color { width:10px;height:10px;border-radius:50%;flex-shrink:0; }
.wfe-node-name  { font-size:12px;font-weight:700;flex:1;word-break:break-word;line-height:1.3; }
.wfe-node-seq   { font-size:10px;font-weight:800;background:#f0f2ff;color:#7986cb;border-radius:5px;padding:2px 5px;flex-shrink:0; }
.wfe-node-tags  { display:flex;flex-wrap:wrap;gap:3px;padding:5px 9px 3px; }
.wfe-tag { font-size:9px;font-weight:800;padding:2px 6px;border-radius:7px; }
.wfe-tag-final   { background:#e8f5e9;color:#2e7d32; }
.wfe-tag-initial { background:#e8eaf6;color:#3949ab; }
.wfe-node-btns  { display:flex;gap:3px;padding:5px 7px 8px; }
.wfe-node-btns button { flex:1;font-size:10px;font-weight:700;padding:4px 0;border-radius:5px;cursor:pointer;border:none;background:#f0f2ff;color:#5c6bc0; }
.wfe-node-btns button:hover { background:#c5cae9; }
.wfe-node-btns button.x { background:#fdecea;color:#c62828; }
.wfe-node-btns button.x:hover { background:#ef9a9a; }
.wfe-conn-handle { position:absolute;right:-11px;top:50%;transform:translateY(-50%);width:22px;height:22px;border-radius:50%;background:#7c8ff7;border:2.5px solid #fff;box-shadow:0 1px 6px #0004;cursor:crosshair;z-index:20;display:flex;align-items:center;justify-content:center;font-size:15px;line-height:1;color:#fff;font-weight:900;opacity:0;transition:opacity .15s,background .12s; }
.wfe-node:hover .wfe-conn-handle { opacity:1; }
.wfe-conn-handle:hover { background:#ff9800; }

.wfe-lrow { display:flex;align-items:center;gap:10px;margin-bottom:6px;color:#3d3d5c;font-size:11px; }
.wfe-lrow:last-child { margin-bottom:0; }

/* empty */
.wfe-empty { position:absolute;inset:0;pointer-events:none;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;color:#b0b8cc; }
.wfe-empty svg { opacity:.2; }
.wfe-empty p { font-size:15px;font-weight:700; }
.wfe-empty small { font-size:12px;opacity:.7; }

/* modal */
.wfe-backdrop { display:none;position:fixed;inset:0;background:#0008;z-index:300;align-items:center;justify-content:center; }
.wfe-backdrop.open { display:flex; }
.wfe-modal { background:#fff;border-radius:16px;padding:24px;width:340px;box-shadow:0 8px 40px #0005; }
.wfe-modal h3 { font-size:15px;font-weight:800;margin-bottom:16px; }
.wfe-fld { margin-top:13px; }
.wfe-fld label { display:block;font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:#9e9e9e;margin-bottom:5px; }
.wfe-fld input[type=text] { width:100%;border:1.5px solid #e4e6ef;border-radius:8px;padding:7px 11px;font-size:13px;outline:none;font-family:inherit; }
.wfe-fld input[type=text]:focus { border-color:#7c8ff7; }
.wfe-color-row { display:flex;align-items:center;gap:8px; }
.wfe-color-row input[type=color] { width:42px;height:34px;padding:2px;border-radius:6px;border:1.5px solid #e4e6ef;cursor:pointer; }
.wfe-color-preview { flex:1;height:34px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:#fff; }
.wfe-toggle-row { display:flex;align-items:center;justify-content:space-between;padding:9px 0;border-bottom:1px solid #f5f5f5; }
.wfe-toggle-row:last-of-type { border-bottom:none; }
.wfe-toggle-info p { font-size:13px;font-weight:600; }
.wfe-toggle-info small { font-size:11px;color:#9e9e9e; }
.wfe-toggle { position:relative;width:38px;height:21px;display:inline-block; }
.wfe-toggle input { opacity:0;width:0;height:0; }
.wfe-slider { position:absolute;inset:0;border-radius:11px;background:#ddd;cursor:pointer;transition:background .2s; }
.wfe-slider::before { content:'';position:absolute;width:15px;height:15px;border-radius:50%;left:3px;top:3px;background:#fff;transition:transform .2s; }
.wfe-toggle input:checked + .wfe-slider { background:#7c8ff7; }
.wfe-toggle input:checked + .wfe-slider::before { transform:translateX(17px); }
.wfe-modal-actions { display:flex;gap:8px;margin-top:18px; }

/* edge popup */
.wfe-edge-popup { position:fixed;z-index:400;background:#fff;border-radius:14px;padding:18px 20px;box-shadow:0 8px 36px #0006;width:320px;display:none; }
.wfe-edge-popup.show { display:block; }
.wfe-edge-popup h4 { font-size:14px;font-weight:800;margin-bottom:4px; }
.wfe-edge-popup .sub { font-size:11px;color:#9e9e9e;margin-bottom:14px; }
.wfe-type-cards { display:flex;flex-direction:column;gap:6px; }
.wfe-type-card { display:flex;align-items:center;gap:12px;padding:10px 14px;border-radius:10px;cursor:pointer;border:2px solid #e8eaf6;transition:border-color .12s,background .12s; }
.wfe-type-card:hover:not(.disabled) { border-color:#9fa8da;background:#f5f6ff; }
.wfe-type-card.disabled { opacity:.4;cursor:not-allowed; }
.wfe-type-card .tc-icon { flex-shrink:0; }
.wfe-type-card .tc-info { flex:1; }
.wfe-type-card .tc-info strong { display:block;font-size:13px; }
.wfe-type-card .tc-info small  { font-size:11px;color:#9e9e9e; }
.wfe-cond-label-area { margin-top:12px;display:none; }
.wfe-cond-label-area.show { display:block; }
.wfe-cond-label-area input { width:100%;border:1.5px solid #e4e6ef;border-radius:8px;padding:8px 11px;font-size:13px;outline:none;margin-bottom:10px;font-family:inherit; }
.wfe-cond-label-area input:focus { border-color:#f57c00; }
.wfe-popup-actions { display:flex;gap:8px;margin-top:12px; }

/* toast */
.wfe-toast { position:fixed;bottom:20px;left:50%;transform:translateX(-50%);background:#323248;color:#fff;border-radius:9px;padding:8px 16px;font-size:12px;font-weight:600;z-index:500;pointer-events:none;opacity:0;transition:opacity .2s;white-space:nowrap; }
.wfe-toast.show { opacity:1; }

/* title bar (breadcrumb + actions) */
.wfe-typebar { padding:6px 12px;background:#fff;border-bottom:1px solid #e4e6ef;font-size:13px;font-weight:600;color:#3d3d5c;display:flex;align-items:center;justify-content:space-between;gap:10px;flex-shrink:0;flex-wrap:wrap; }
.wfe-typebar-title { display:flex;align-items:center;gap:8px;min-width:0;flex:1; }
.wfe-typebar-title > span { color:#9fa8da;font-weight:400; }
.wfe-typebar-title strong { overflow:hidden;text-overflow:ellipsis;white-space:nowrap; }
.wfe-typebar-actions { display:flex;align-items:center;gap:6px;flex-wrap:wrap;justify-content:flex-end; }
.wfe-typebar .btn { padding:4px 10px;font-size:11px;border-radius:7px; }
.wfe-typebar .btn-ghost { color:#5c6078;border:1.5px solid #cfd2e8;background:transparent; }
.wfe-typebar .btn-ghost:hover { border-color:#7c8ff7;color:#3d3d5c; }
.wfe-typebar-sep { width:1px;height:18px;background:#e0e3ef;margin:0 2px;flex-shrink:0; }
.wfe-typebar .wfe-zoom-pct { font-size:11px;font-weight:800;color:#5c6078;min-width:36px;text-align:center; }
`;

const CSS_DARK = `
.wfe-root.wfe-root--dark { background:#12171f; color:#e8eaf4; }
.wfe-root.wfe-root--dark .btn-ghost { color:#a8b9ff!important; border-color:rgba(148,163,227,0.45)!important; }
.wfe-root.wfe-root--dark .btn-layout { background:rgba(28,34,44,0.92)!important; color:#e0e7ff!important; border-color:rgba(255,255,255,0.12)!important; }
.wfe-root.wfe-root--dark .wfe-sidebar { background:#1c222c; border-right-color:rgba(255,255,255,0.08); }
.wfe-root.wfe-root--dark .wfe-sidebar-legend { border-bottom-color:rgba(255,255,255,0.08); background:rgba(25,31,41,0.95); }
.wfe-root.wfe-root--dark .wfe-sidebar-legend .wfe-lrow { color:#cfd5e9; }
.wfe-root.wfe-root--dark .wfe-sidebar-head { border-bottom-color:rgba(255,255,255,0.08); }
.wfe-root.wfe-root--dark .wfe-sidebar-head h2 { color:#aab3cc; }
.wfe-root.wfe-root--dark .wfe-sidebar-hint { color:#8b93a8; }
.wfe-root.wfe-root--dark .wfe-chip { background:#242b36; border-color:rgba(255,255,255,0.08); color:#eef1fb; }
.wfe-root.wfe-root--dark .wfe-chip:hover { box-shadow:0 2px 10px rgba(0,0,0,0.45); border-color:rgba(148,163,227,0.45); }
.wfe-root.wfe-root--dark .wfe-chip.in-flow { background:#1e2740; border-color:rgba(100,149,237,0.35); opacity:0.92; }
.wfe-root.wfe-root--dark .wfe-chip .badge { background:#2d3748; color:#a8b9ff; }
.wfe-root.wfe-root--dark .wfe-canvas-wrap { background:#181e28; background-image:radial-gradient(circle,rgba(255,255,255,0.08) 1.2px,transparent 1.2px); }
.wfe-root.wfe-root--dark .wfe-canvas-wrap.drag-over { background-color:#1f2836; }
.wfe-root.wfe-root--dark .wfe-node { background:#252d3a; border-color:rgba(255,255,255,0.12); box-shadow:0 3px 16px rgba(0,0,0,0.45); color:#eef1fb; }
.wfe-root.wfe-root--dark .wfe-node:hover { border-color:rgba(148,163,227,0.55); box-shadow:0 4px 22px rgba(0,0,0,0.55); }
.wfe-root.wfe-root--dark .wfe-node-top { border-bottom-color:rgba(255,255,255,0.06); }
.wfe-root.wfe-root--dark .wfe-node-seq { background:#323b4f; color:#b4c7ff; }
.wfe-root.wfe-root--dark .wfe-node-btns button { background:#2a3347; color:#cdd8ff; }
.wfe-root.wfe-root--dark .wfe-node-btns button:hover { background:#3b4760; }
.wfe-root.wfe-root--dark .wfe-lrow { color:#cbd3e8; }
.wfe-root.wfe-root--dark .wfe-empty { color:#7c869a; }
.wfe-root.wfe-root--dark .wfe-typebar { background:#1c222c; border-bottom-color:rgba(255,255,255,0.08); color:#e8eaf4; }
.wfe-root.wfe-root--dark .wfe-typebar-title > span:first-child { color:#9eabcc; }
.wfe-root.wfe-root--dark .wfe-typebar .btn-ghost { color:#b8c7ff!important; border-color:rgba(255,255,255,0.14)!important; }
.wfe-root.wfe-root--dark .wfe-typebar .btn-ghost:hover { border-color:#7c8ff7!important; color:#f0f3ff!important; }
.wfe-root.wfe-root--dark .wfe-typebar-sep { background:rgba(255,255,255,0.12); }
.wfe-root.wfe-root--dark .wfe-typebar .wfe-zoom-pct { color:#b4bfd9; }
.wfe-root.wfe-root--dark .wfe-modal { background:#252d3a; color:#eef1fb; box-shadow:0 12px 48px rgba(0,0,0,0.55); }
.wfe-root.wfe-root--dark .wfe-fld label { color:#9eabcc!important; }
.wfe-root.wfe-root--dark .wfe-fld input[type=text] { border-color:rgba(255,255,255,0.14); background:#1b222c; color:#eef1fb; }
.wfe-root.wfe-root--dark .wfe-toggle-row { border-bottom-color:rgba(255,255,255,0.06); }
.wfe-root.wfe-root--dark .wfe-toggle-info small { color:#8b939e!important; }
.wfe-root.wfe-root--dark .wfe-edge-popup { background:#252d3a; color:#eef1fb; box-shadow:0 12px 40px rgba(0,0,0,0.55); border:1px solid rgba(255,255,255,0.06); }
.wfe-root.wfe-root--dark .wfe-edge-popup .sub { color:#9eabcc!important; }
.wfe-root.wfe-root--dark .wfe-type-card { border-color:rgba(255,255,255,0.1)!important; background:#1f2733; }
.wfe-root.wfe-root--dark .wfe-type-card:hover:not(.disabled) { border-color:rgba(148,163,227,0.55)!important; background:#2a3348; }
.wfe-root.wfe-root--dark .wfe-type-card .tc-info small { color:#9eabcc!important; }
.wfe-root.wfe-root--dark .wfe-cond-label-area input { border-color:rgba(255,255,255,0.14); background:#1b222c; color:#eef1fb; }
`;

function wfeRootClassName(readOnly, isDark) {
  let c = 'wfe-root';
  if (readOnly) c += ' wfe-root--inline';
  if (isDark) c += ' wfe-root--dark';
  return c;
}

/* ─── Helper: inject/cleanup style ─────────────────────────────────────── */
function useInjectStyle(css) {
  useEffect(() => {
    const el = document.createElement('style');
    el.id = 'wfe-style';
    el.textContent = css;
    document.head.appendChild(el);
    return () => el.remove();
  }, [css]);
}

/* ─── Main component ────────────────────────────────────────────────────── */
export default function WorkflowEditorPage({ typeId: typeIdProp, readOnly: readOnlyProp } = {}) {
  const { typeId: routeTypeId } = useParams();
  const typeId = typeIdProp ?? routeTypeId;
  const readOnly = !!readOnlyProp;
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const wfeCombinedCss = useMemo(
    () => (isDarkMode ? `${CSS}\n${CSS_DARK}` : CSS),
    [isDarkMode],
  );
  useInjectStyle(wfeCombinedCss);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [typeName, setTypeName] = useState('');
  const catalogRef = useRef([]);          // full status definitions

  /* ── Canvas state (mutable refs, not React state) ── */
  const nodesRef = useRef([]);            // { id, catalogId, name, color, x, y, showForm }
  const edgesRef = useRef([]);            // { id, from, to, type, label }
  const nidRef   = useRef(1);
  const eidRef   = useRef(1);

  /* ── Interaction refs ── */
  const moveDragRef      = useRef(null);
  const handleDragRef    = useRef(null);
  const dragCatalogIdRef = useRef(null);
  const pendingEdgeRef   = useRef(null);
  const pendingTypeRef   = useRef(null);
  const editingIdRef     = useRef(null);
  const toastTimerRef    = useRef(null);
  const hintActiveRef    = useRef(false);
  const hoverNodeHighlightRef = useRef(null);

  /* ── DOM refs ── */
  const wrapRef    = useRef(null);
  const canvasRef  = useRef(null);
  const svgRef     = useRef(null);
  const sidebarRef = useRef(null);
  const emptyRef   = useRef(null);
  const toastRef   = useRef(null);
  const modalRef   = useRef(null);
  const popupRef   = useRef(null);
  const panMidRef  = useRef(null);
  const viewZoomRef = useRef(1);

  const [viewZoom, setViewZoom] = useState(1);

  /** Pointer position in unscaled canvas coordinates (matches node.x / node.y). */
  function eventToCanvasLocal(e) {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const r = canvas.getBoundingClientRect();
    const z = viewZoomRef.current || 1;
    return {
      x: (e.clientX - r.left) / z,
      y: (e.clientY - r.top) / z,
    };
  }

  function setZoomClamped(next) {
    const nz = Math.min(2.5, Math.max(0.35, next));
    viewZoomRef.current = nz;
    setViewZoom(nz);
  }

  /* ════════════════════════════════════════════════════════════════════════
     CANVAS LOGIC (translated verbatim from workflow-draft.html)
  ════════════════════════════════════════════════════════════════════════ */

  const derivedRoles = useCallback(() => {
    const nodes = nodesRef.current;
    const edges = edgesRef.current;
    const primaryEdges = edges.filter(e => e.type === 'primary');
    const hasIncoming = new Set(primaryEdges.map(e => e.to));
    const hasOutgoing = new Set(primaryEdges.map(e => e.from));
    const anyIncoming = new Set(edges.map(e => e.to));
    const anyOutgoing = new Set(edges.map(e => e.from));
    const initialCandidates = nodes.filter(n => !hasIncoming.has(n.id) && hasOutgoing.has(n.id));
    return {
      initialId: initialCandidates[0]?.id ?? null,
      finalIds:  nodes.filter(n => !hasOutgoing.has(n.id)).map(n => n.id),
      isolatedIds: nodes.filter(n => !anyIncoming.has(n.id) && !anyOutgoing.has(n.id)).map(n => n.id),
    };
  }, []);

  const nodeGeom = useCallback((node) => {
    const el = document.getElementById('wfe-node-' + node.id);
    if (!el) return null;
    const w = el.offsetWidth, h = el.offsetHeight;
    return { x: node.x, y: node.y, w, h, cx: node.x + w/2, cy: node.y + h/2 };
  }, []);

  const calcPath = useCallback((fg, tg) => {
    const dx = tg.cx - fg.cx, dy = tg.cy - fg.cy;
    const ang = Math.atan2(dy, dx);
    const x1 = fg.cx + Math.cos(ang) * (fg.w/2 + 5);
    const y1 = fg.cy + Math.sin(ang) * (fg.h/2 + 5);
    const x2 = tg.cx - Math.cos(ang) * (tg.w/2 + 16);
    const y2 = tg.cy - Math.sin(ang) * (tg.h/2 + 16);
    const mx = (x1+x2)/2, my = (y1+y2)/2;
    const len = Math.sqrt(dx*dx + dy*dy);
    const bow = Math.min(55, len * 0.16);
    const perp = Math.atan2(-dx, dy);
    return { d: `M ${x1} ${y1} Q ${mx+Math.cos(perp)*bow} ${my+Math.sin(perp)*bow} ${x2} ${y2}`, lx: mx, ly: my };
  }, []);

  const ESTYLE = {
    primary:     { stroke:'#3949ab', width:4, dash:null,  mEnd:'url(#wfe-m-pri)',   mStart:null,              mEndH:'url(#wfe-m-pri-h)' },
    alternative: { stroke:'#607d8b', width:2, dash:null,  mEnd:'url(#wfe-m-alt)',   mStart:null,              mEndH:'url(#wfe-m-alt-h)' },
    conditional: { stroke:'#f57c00', width:2, dash:'7 3', mEnd:'url(#wfe-m-con-e)', mStart:'url(#wfe-m-con-s)',mEndH:'url(#wfe-m-con-h)' },
  };

  const toast = useCallback((msg, dur=2400) => {
    hintActiveRef.current = false;
    const el = toastRef.current;
    if (!el) return;
    el.textContent = msg;
    el.style.background = '';
    el.classList.add('show');
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => el.classList.remove('show'), dur);
  }, []);

  const showHint = useCallback((msg) => {
    hintActiveRef.current = true;
    const el = toastRef.current;
    if (!el) return;
    el.textContent = msg;
    el.classList.add('show');
  }, []);

  const hideHint = useCallback(() => {
    if (!hintActiveRef.current) return;
    hintActiveRef.current = false;
    toastRef.current?.classList.remove('show');
  }, []);

  const applyNodeEdgeHighlight = useCallback((nodeId) => {
    const svg = svgRef.current;
    if (!svg) return;
    const groups = svg.querySelectorAll('.wfe-eg');
    groups.forEach((g) => {
      const vis = g.querySelector('.wfe-edge-vis');
      if (!vis) return;
      const from = g.getAttribute('data-from');
      const to = g.getAttribute('data-to');
      const typ = g.getAttribute('data-edge-type') || 'primary';
      const baseStroke = vis.getAttribute('data-stroke-base') || '#3949ab';
      const baseWidth = parseFloat(vis.getAttribute('data-width-base') || '2', 10);
      const mEnd = vis.getAttribute('data-marker-end-base') || '';
      const mStart = vis.getAttribute('data-marker-start-base');
      const w = String(baseWidth);

      if (!nodeId) {
        g.style.opacity = '1';
        vis.setAttribute('stroke', baseStroke);
        vis.setAttribute('stroke-width', w);
        if (mEnd) vis.setAttribute('marker-end', mEnd);
        if (mStart) vis.setAttribute('marker-start', mStart);
        else vis.removeAttribute('marker-start');
        return;
      }

      const connected = from === nodeId || to === nodeId;
      if (connected) {
        g.style.opacity = '1';
        let hlStroke = baseStroke;
        if (typ === 'primary') hlStroke = '#1a237e';
        else if (typ === 'alternative') hlStroke = baseStroke === '#e2e6ef' ? '#90a4ae' : '#37474f';
        else if (typ === 'conditional') hlStroke = '#f4511e';
        const extra = typ === 'primary' ? 3 : 1.5;
        vis.setAttribute('stroke', hlStroke);
        vis.setAttribute('stroke-width', String(baseWidth + extra));
        if (mEnd) vis.setAttribute('marker-end', mEnd);
        if (mStart) vis.setAttribute('marker-start', mStart);
        else vis.removeAttribute('marker-start');
      } else {
        g.style.opacity = '0.18';
        vis.setAttribute('stroke', baseStroke);
        vis.setAttribute('stroke-width', w);
        if (mEnd) vis.setAttribute('marker-end', mEnd);
        if (mStart) vis.setAttribute('marker-start', mStart);
        else vis.removeAttribute('marker-start');
      }
    });
  }, []);

  const drawEdges = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;
    svg.querySelectorAll('.wfe-eg').forEach(e => e.remove());

    const incomingAltToTarget = {};
    edgesRef.current.forEach(e => {
      if (e.type === 'alternative') {
        incomingAltToTarget[e.to] = (incomingAltToTarget[e.to] || 0) + 1;
      }
    });

    edgesRef.current.forEach(edge => {
      const fn = nodesRef.current.find(n => n.id === edge.from);
      const tn = nodesRef.current.find(n => n.id === edge.to);
      if (!fn || !tn) return;
      const fg = nodeGeom(fn), tg = nodeGeom(tn);
      if (!fg || !tg) return;
      const { d, lx, ly } = calcPath(fg, tg);
      const st = ESTYLE[edge.type] || ESTYLE.primary;
      const fadeCrowdedAlt =
        edge.type === 'alternative' && (incomingAltToTarget[edge.to] || 0) >= 3;
      const strokeColor = fadeCrowdedAlt ? '#e2e6ef' : st.stroke;

      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.classList.add('wfe-eg');
      g.setAttribute('data-from', edge.from);
      g.setAttribute('data-to', edge.to);
      g.setAttribute('data-edge-type', edge.type || 'primary');

      const hit = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      hit.setAttribute('d', d);
      hit.setAttribute('fill', 'none');
      hit.setAttribute('stroke', 'transparent');
      hit.setAttribute('stroke-width', '18');
      hit.style.cursor = 'pointer';
      hit.style.pointerEvents = 'stroke';

      const vis = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      vis.classList.add('wfe-edge-vis');
      vis.setAttribute('d', d);
      vis.setAttribute('fill', 'none');
      vis.setAttribute('stroke', strokeColor);
      vis.setAttribute('stroke-width', st.width);
      if (st.dash) vis.setAttribute('stroke-dasharray', st.dash);
      vis.setAttribute('marker-end', st.mEnd);
      if (st.mStart) vis.setAttribute('marker-start', st.mStart);
      vis.setAttribute('data-stroke-base', strokeColor);
      vis.setAttribute('data-width-base', String(st.width));
      vis.setAttribute('data-marker-end-base', st.mEnd);
      if (st.mStart) vis.setAttribute('data-marker-start-base', st.mStart);
      else vis.removeAttribute('data-marker-start-base');
      vis.style.pointerEvents = 'none';

      let labelGroup = null;
      if (edge.label) {
        labelGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        labelGroup.style.pointerEvents = 'none';
        const lw = edge.label.length * 6.5 + 16;
        const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        bg.setAttribute('x', lx - lw/2); bg.setAttribute('y', ly - 12);
        bg.setAttribute('width', lw); bg.setAttribute('height', 17);
        bg.setAttribute('rx', '5'); bg.setAttribute('fill', '#fff3e0');
        bg.setAttribute('stroke', '#f57c00'); bg.setAttribute('stroke-width', '1');
        const txt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        txt.setAttribute('x', lx); txt.setAttribute('y', ly + 1);
        txt.setAttribute('text-anchor', 'middle'); txt.setAttribute('font-size', '10');
        txt.setAttribute('font-weight', '700'); txt.setAttribute('fill', '#e65100');
        txt.setAttribute('font-family', 'Segoe UI,system-ui,sans-serif');
        txt.textContent = edge.label;
        labelGroup.appendChild(bg); labelGroup.appendChild(txt);
      }

      hit.addEventListener('mouseenter', () => {
        g.style.opacity = '1';
        vis.setAttribute('stroke', '#e53e3e');
        vis.setAttribute('marker-end', st.mEndH);
        const fn2 = nodesRef.current.find(n => n.id === edge.from);
        const tn2 = nodesRef.current.find(n => n.id === edge.to);
        showHint(`Clique para remover: ${fn2?.name} → ${tn2?.name}`);
      });
      hit.addEventListener('mouseleave', () => {
        hideHint();
        applyNodeEdgeHighlight(hoverNodeHighlightRef.current);
      });
      hit.addEventListener('click', () => {
        const fn2 = nodesRef.current.find(n => n.id === edge.from);
        const tn2 = nodesRef.current.find(n => n.id === edge.to);
        const fromL = (fn2?.name || '').trim() || '?';
        const toL = (tn2?.name || '').trim() || '?';
        if (!window.confirm(`Remover a seta ${fromL} → ${toL}?`)) return;
        edgesRef.current = edgesRef.current.filter(e => e.id !== edge.id);
        toast('🗑️ Seta removida');
        render();
      });

      g.appendChild(hit); g.appendChild(vis);
      if (labelGroup) g.appendChild(labelGroup);
      svg.appendChild(g);
    });

    if (hoverNodeHighlightRef.current) applyNodeEdgeHighlight(hoverNodeHighlightRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcPath, nodeGeom, showHint, hideHint, toast, applyNodeEdgeHighlight]);

  const renderSidebar = useCallback(() => {
    const list = sidebarRef.current;
    if (!list) return;
    const esc = (s) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/"/g, '&quot;');
    list.innerHTML = '';
    catalogRef.current.forEach(def => {
      const inNode = nodesRef.current.find(n => n.catalogId === def.id);
      const inFlow = Boolean(inNode);
      const flowLabel = inFlow ? (inNode.name || def.name) : def.name;
      const flowColor =
        inFlow && String(inNode.color || '').trim() ? inNode.color : def.color || '#607d8b';
      const el = document.createElement('div');
      el.className = 'wfe-chip' + (inFlow ? ' in-flow' : '');
      el.draggable = !inFlow;
      el.title = inFlow
        ? flowLabel !== def.name
          ? `No fluxo como «${String(flowLabel).replace(/"/g, "'")}» (catálogo: «${String(def.name).replace(/"/g, "'")}»)`
          : 'Já está no fluxo'
        : 'Arraste para o diagrama';
      el.innerHTML = `
        <span class="wfe-drag-icon">${inFlow ? '✓' : '⠿'}</span>
        <div class="dot" style="background:${esc(flowColor)}"></div>
        <span class="lbl">${esc(flowLabel)}</span>
        <span class="badge">${inFlow ? 'no fluxo' : 'arrastar'}</span>
      `;
      if (!inFlow) {
        el.addEventListener('dragstart', e => {
          dragCatalogIdRef.current = def.id;
          e.dataTransfer.effectAllowed = 'copy';
          e.dataTransfer.setData('text/plain', def.id);
        });
        el.addEventListener('dragend', () => { dragCatalogIdRef.current = null; });
      }
      list.appendChild(el);
    });
  }, []);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.querySelectorAll('.wfe-node').forEach(e => e.remove());

    const { initialId, finalIds } = derivedRoles();

    nodesRef.current.forEach((node, idx) => {
      const isInitial = node.id === initialId;
      const isFinal   = finalIds.includes(node.id);

      const el = document.createElement('div');
      el.className = 'wfe-node'
        + (isFinal   ? ' is-final'   : '')
        + (isInitial ? ' is-initial' : '');
      el.id = 'wfe-node-' + node.id;
      el.style.cssText = `left:${node.x}px;top:${node.y}px`;

      const tagList = [];
      if (isInitial)     tagList.push('<span class="wfe-tag wfe-tag-initial">🚀 Inicial</span>');
      if (isFinal)       tagList.push('<span class="wfe-tag wfe-tag-final">🏁 Final</span>');
      if (node.showForm) tagList.push('<span class="wfe-tag" style="background:#f3e5f5;color:#6a1b9a">📋 Formulário</span>');
      const tags = tagList.length ? `<div class="wfe-node-tags">${tagList.join('')}</div>` : '';

      el.innerHTML = `
        <div class="wfe-node-top">
          <div class="wfe-node-color" style="background:${node.color}"></div>
          <div class="wfe-node-name">${node.name}</div>
          <div class="wfe-node-seq">#${idx+1}</div>
        </div>
        ${tags}
        ${!readOnly ? `<div class="wfe-node-btns">
          <button data-edit="${node.id}">✏️ Editar</button>
          <button class="x" data-remove="${node.id}">✕</button>
        </div>
        <div class="wfe-conn-handle" data-handle="${node.id}" title="Arraste para criar seta">+</div>` : ''}
      `;

      if (!readOnly) {
        el.querySelector('.wfe-node-top').addEventListener('mousedown', ev => startMove(ev, node.id));
        el.querySelector('[data-edit]').addEventListener('click', ev => { ev.stopPropagation(); openModal(node.id); });
        el.querySelector('[data-remove]').addEventListener('click', ev => { ev.stopPropagation(); removeNode(node.id); });
        el.querySelector('.wfe-conn-handle').addEventListener('mousedown', ev => { ev.stopPropagation(); startHandleDrag(ev, node.id); });
      }

      el.addEventListener('mouseenter', () => {
        hoverNodeHighlightRef.current = node.id;
        applyNodeEdgeHighlight(node.id);
      });
      el.addEventListener('mouseleave', (ev) => {
        const rt = ev.relatedTarget;
        if (rt && el.contains(rt)) return;
        if (rt && typeof rt.closest === 'function') {
          const eg = rt.closest('.wfe-eg');
          if (eg) {
            const f = eg.getAttribute('data-from');
            const t = eg.getAttribute('data-to');
            if (f === node.id || t === node.id) return;
          }
        }
        hoverNodeHighlightRef.current = null;
        applyNodeEdgeHighlight(null);
      });

      canvas.appendChild(el);
    });

    drawEdges();
    if (emptyRef.current) emptyRef.current.style.display = nodesRef.current.length === 0 ? 'flex' : 'none';
    renderSidebar();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedRoles, drawEdges, renderSidebar, applyNodeEdgeHighlight]);

  /* ── Node move ── */
  function startMove(e, nodeId) {
    const node = nodesRef.current.find(n => n.id === nodeId);
    const { x, y } = eventToCanvasLocal(e);
    moveDragRef.current = { nodeId, ox: x - node.x, oy: y - node.y };
    e.preventDefault();
  }

  /* ── Handle drag ── */
  function startHandleDrag(e, fromId) {
    handleDragRef.current = { fromId };
    const preview = document.getElementById('wfe-preview-line');
    if (preview) preview.style.display = '';
    updatePreviewLine(e);
    e.preventDefault();
  }
  function updatePreviewLine(e) {
    const fn = nodesRef.current.find(n => n.id === handleDragRef.current?.fromId);
    if (!fn) return;
    const fg = nodeGeom(fn);
    if (!fg) return;
    const { x: tx, y: ty } = eventToCanvasLocal(e);
    const preview = document.getElementById('wfe-preview-line');
    if (preview) preview.setAttribute('d', `M ${fg.cx} ${fg.cy} L ${tx} ${ty}`);
  }
  function finishHandleDrag(e) {
    const preview = document.getElementById('wfe-preview-line');
    if (preview) { preview.setAttribute('d', ''); preview.style.display = 'none'; }
    const { x: mx, y: my } = eventToCanvasLocal(e);
    let targetId = null;
    for (const node of nodesRef.current) {
      if (node.id === handleDragRef.current?.fromId) continue;
      const g = nodeGeom(node);
      if (g && mx >= g.x && mx <= g.x+g.w && my >= g.y && my <= g.y+g.h) { targetId = node.id; break; }
    }
    const fromId = handleDragRef.current?.fromId;
    handleDragRef.current = null;
    if (targetId) openEdgePopup(fromId, targetId, e.clientX, e.clientY);
    else toast('Solte sobre outro card para criar a seta');
  }

  /* ── Remove node ── */
  function removeNode(nodeId) {
    const n = nodesRef.current.find(x => x.id === nodeId);
    const label = (n?.name || '').trim() || 'este status';
    if (!window.confirm(`Remover "${label}" deste fluxo? As setas ligadas a ele serão removidas.`)) return;
    nodesRef.current = nodesRef.current.filter(x => x.id !== nodeId);
    edgesRef.current = edgesRef.current.filter(e => e.from !== nodeId && e.to !== nodeId);
    if (editingIdRef.current === nodeId) closeModal();
    render();
  }

  /* ── Auto layout ── */
  function autoLayout() {
    const cols = Math.max(2, Math.ceil(Math.sqrt(nodesRef.current.length)));
    nodesRef.current.forEach((n, i) => {
      n.x = 60 + (i % cols) * 228;
      n.y = 70 + Math.floor(i / cols) * 150;
    });
    render();
  }

  /* ── Edge popup ── */
  function openEdgePopup(fromId, toId, cx, cy) {
    if (edgesRef.current.find(e => e.from === fromId && e.to === toId)) { toast('Esta seta já existe'); return; }
    pendingEdgeRef.current = { fromId, toId };
    pendingTypeRef.current = null;
    const hasPrimary = edgesRef.current.some(e => e.from === fromId && e.type === 'primary');
    const popup = popupRef.current;
    if (!popup) return;
    const tc = popup.querySelector('#wfe-tc-primary');
    const hint = popup.querySelector('#wfe-tc-primary-hint');
    if (hasPrimary) {
      tc?.classList.add('disabled');
      if (hint) hint.textContent = '⚠️ Este card já tem seta principal';
    } else {
      tc?.classList.remove('disabled');
      if (hint) hint.textContent = 'Caminho padrão — 1 por card';
    }
    popup.querySelector('.wfe-cond-label-area')?.classList.remove('show');
    const condInput = popup.querySelector('#wfe-cond-input');
    if (condInput) condInput.value = '';
    const cancelRow = popup.querySelector('#wfe-ep-cancel-row');
    if (cancelRow) cancelRow.style.display = 'flex';
    popup.style.left = Math.min(cx - 160, window.innerWidth  - 340) + 'px';
    popup.style.top  = Math.min(cy - 80,  window.innerHeight - 420) + 'px';
    popup.classList.add('show');
  }
  function chooseEdgeType(type) {
    if (type === 'primary') {
      const hasPrimary = edgesRef.current.some(e => e.from === pendingEdgeRef.current?.fromId && e.type === 'primary');
      if (hasPrimary) { toast('⚠️ Já existe uma seta principal saindo deste card'); return; }
    }
    if (type === 'conditional') {
      pendingTypeRef.current = 'conditional';
      popupRef.current?.querySelector('.wfe-cond-label-area')?.classList.add('show');
      const cancelRow = popupRef.current?.querySelector('#wfe-ep-cancel-row');
      if (cancelRow) cancelRow.style.display = 'none';
      setTimeout(() => popupRef.current?.querySelector('#wfe-cond-input')?.focus(), 50);
      return;
    }
    commitEdge(type, '');
  }
  function confirmConditional() {
    const label = (popupRef.current?.querySelector('#wfe-cond-input')?.value || '').trim();
    commitEdge('conditional', label);
  }
  function commitEdge(type, label) {
    edgesRef.current.push({ id: 'e'+(eidRef.current++), from: pendingEdgeRef.current.fromId, to: pendingEdgeRef.current.toId, type, label });
    pendingEdgeRef.current = null; pendingTypeRef.current = null;
    popupRef.current?.classList.remove('show');
    toast('✅ Seta criada!');
    render();
  }
  function cancelEdgePopup() {
    pendingEdgeRef.current = null; pendingTypeRef.current = null;
    popupRef.current?.classList.remove('show');
    popupRef.current?.querySelector('.wfe-cond-label-area')?.classList.remove('show');
  }

  /* ── Node modal ── */
  function openModal(nodeId) {
    const n = nodesRef.current.find(n => n.id === nodeId);
    if (!n || !modalRef.current) return;
    editingIdRef.current = nodeId;
    modalRef.current.querySelector('#wfe-m-name').value   = n.name;
    modalRef.current.querySelector('#wfe-m-color').value  = n.color;
    modalRef.current.querySelector('#wfe-m-form').checked = n.showForm;
    syncColorPreview();
    const { initialId, finalIds } = derivedRoles();
    const isInitial = nodeId === initialId;
    const isFinal   = finalIds.includes(nodeId);
    const infoEl    = modalRef.current.querySelector('#wfe-m-derived-info');
    const textEl    = modalRef.current.querySelector('#wfe-m-derived-text');
    const roles = [];
    if (isInitial) roles.push('🚀 <b>Status Inicial</b> — nenhuma seta principal chega aqui');
    if (isFinal)   roles.push('🏁 <b>Status Final</b> — nenhuma seta principal sai daqui');
    if (roles.length) { textEl.innerHTML = roles.join(' &nbsp;·&nbsp; '); infoEl.style.display = 'block'; }
    else { infoEl.style.display = 'none'; }
    modalRef.current.querySelector('.wfe-backdrop').classList.add('open');
  }
  function closeModal() {
    modalRef.current?.querySelector('.wfe-backdrop').classList.remove('open');
    editingIdRef.current = null;
  }
  function syncColorPreview() {
    const c = modalRef.current?.querySelector('#wfe-m-color')?.value;
    const p = modalRef.current?.querySelector('#wfe-m-preview');
    if (p) { p.style.background = c; p.textContent = c; }
  }
  function saveNodeModal() {
    const n = nodesRef.current.find(n => n.id === editingIdRef.current);
    if (!n) return;
    n.name     = modalRef.current.querySelector('#wfe-m-name').value;
    n.color    = modalRef.current.querySelector('#wfe-m-color').value;
    n.showForm = modalRef.current.querySelector('#wfe-m-form').checked;
    closeModal(); render();
  }
  function removeFromFlow() { removeNode(editingIdRef.current); closeModal(); }

  /* ── Drag to canvas ── */
  function onDragOver(e) {
    if (!dragCatalogIdRef.current) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    wrapRef.current?.classList.add('drag-over');
  }
  function onDragLeave() { wrapRef.current?.classList.remove('drag-over'); }
  function onDrop(e) {
    e.preventDefault();
    wrapRef.current?.classList.remove('drag-over');
    const cid = dragCatalogIdRef.current || e.dataTransfer.getData('text/plain');
    if (!cid) return;
    if (nodesRef.current.some(n => n.catalogId === cid)) { toast('Esse status já está no fluxo'); return; }
    const def = catalogRef.current.find(d => d.id === cid);
    if (!def) return;
    const { x: cx, y: cy } = eventToCanvasLocal(e);
    nodesRef.current.push({
      id: 'n'+(nidRef.current++), catalogId: cid, name: def.name, color: def.color,
      x: Math.max(10, cx - 86),
      y: Math.max(10, cy - 52),
      showForm: false,
    });
    render();
  }

  /* ── Validate & Save ── */
  async function saveWorkflow() {
    if (nodesRef.current.length === 0) {
      showValidationError('⚠️ O fluxo está vazio. Adicione ao menos um status.');
      return;
    }
    const { initialId, finalIds, isolatedIds } = derivedRoles();
    if (isolatedIds.length > 0) {
      const names = isolatedIds.map(id => nodesRef.current.find(n => n.id === id)?.name || id).join(', ');
      showValidationError(`⚠️ Status sem nenhuma seta (desconectados do diagrama): ${names}`);
      return;
    }
    if (!initialId) {
      showValidationError('⚠️ Nenhum Status Inicial. Um card deve ter seta principal saindo mas nenhuma chegando.');
      return;
    }
    if (finalIds.length === 0) {
      showValidationError('⚠️ Nenhum Status Final. Ao menos um card deve não ter seta principal saindo.');
      return;
    }

    setSaving(true);
    try {
      const normHex = (c) => (c || '').trim().replace(/^#/, '').toLowerCase();
      const statuses = nodesRef.current.map(n => {
        const def = catalogRef.current.find(d => d.id === n.catalogId);
        const catalogName = (def?.name || '').trim();
        const catalogColor = (def?.color || '#607d8b').trim();
        const nameTrim = (n.name || '').trim();
        const colorTrim = (n.color || '').trim();
        const row = {
          status_definition_id: n.catalogId,
          show_customization_form: n.showForm,
          canvas_x: n.x,
          canvas_y: n.y,
        };
        if (nameTrim !== catalogName) row.display_name = nameTrim;
        if (normHex(colorTrim) !== normHex(catalogColor)) row.display_color = colorTrim;
        return row;
      });
      const transitions = edgesRef.current.map(e => ({
        from_status_id: nodesRef.current.find(n => n.id === e.from)?.catalogId,
        to_status_id:   nodesRef.current.find(n => n.id === e.to)?.catalogId,
        transition_type: e.type,
        label: e.label || '',
      })).filter(t => t.from_status_id && t.to_status_id);

      await replaceOrderTypeStatuses(typeId, { statuses, transitions });
      navigate('/settings/order-workflow/types', { replace: true });
    } catch (err) {
      showValidationError('❌ ' + (err.message || 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  }

  function showValidationError(msg) {
    const el = toastRef.current;
    if (!el) return;
    el.textContent = msg;
    el.style.background = '#c62828';
    el.classList.add('show');
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      el.classList.remove('show');
      el.style.background = '';
    }, 4000);
  }

  /* ════════════════════════════════════════════════════════════════════════
     DATA LOADING
  ════════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (!typeId) return;
    let cancelled = false;
    (async () => {
      try {
        const [defsRes, statusesRes, typesRes] = await Promise.all([
          listOrderStatusDefinitions(),
          getOrderTypeStatuses(typeId),
          listOrderTypes(),
        ]);
        if (cancelled) return;

        nodesRef.current = [];
        edgesRef.current = [];
        nidRef.current = 1;
        eidRef.current = 1;

        catalogRef.current = (defsRes.data || []).map(d => ({
          id: d.id,
          name: d.name,
          color: d.color || '#607d8b',
        }));

        const t = (typesRes.data || []).find(t => t.id === typeId);
        setTypeName(t?.name || typeId);

        const payload = statusesRes.data || {};
        const statuses    = Array.isArray(payload) ? payload : (payload.statuses || []);
        const transitions = Array.isArray(payload) ? [] : (payload.transitions || []);

        // Build nodes from statuses
        statuses.forEach((s, i) => {
          const nodeId = 'n' + (nidRef.current++);
          nodesRef.current.push({
            id: nodeId,
            catalogId: s.status_definition_id,
            name: s.name,
            color: s.color || '#607d8b',
            x: s.canvas_x != null ? s.canvas_x : 60 + (i % 3) * 228,
            y: s.canvas_y != null ? s.canvas_y : 70 + Math.floor(i / 3) * 150,
            showForm: !!s.show_customization_form,
          });
        });

        // Build edges from transitions (map catalogId → nodeId)
        const catalogIdToNodeId = {};
        nodesRef.current.forEach(n => { catalogIdToNodeId[n.catalogId] = n.id; });
        transitions.forEach(t => {
          const fromNodeId = catalogIdToNodeId[t.from_status_id];
          const toNodeId   = catalogIdToNodeId[t.to_status_id];
          if (fromNodeId && toNodeId) {
            edgesRef.current.push({
              id: 'e' + (eidRef.current++),
              from: fromNodeId,
              to: toNodeId,
              type: t.transition_type || 'primary',
              label: t.label || '',
            });
          }
        });

        if (cancelled) return;
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Erro ao carregar');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId]);

  /* ── Render canvas once data is loaded ── */
  useEffect(() => {
    if (!loading) {
      render();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  /* ── Canvas zoom (Ctrl/Cmd + scroll wheel) ── */
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || loading) return;
    c.style.transform = `scale(${viewZoom})`;
  }, [viewZoom, loading]);

  useEffect(() => {
    if (loading) return;
    const wrap = wrapRef.current;
    if (!wrap) return;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const z = viewZoomRef.current;
      setZoomClamped(z * (e.deltaY > 0 ? 0.9 : 1.1));
    };
    wrap.addEventListener('wheel', onWheel, { passive: false });
    return () => wrap.removeEventListener('wheel', onWheel);
  }, [loading]);

  /* ── Global mouse events ── */
  useEffect(() => {
    const onMouseMove = (e) => {
      if (panMidRef.current) {
        const wrap = wrapRef.current;
        if (wrap) {
          wrap.scrollLeft -= e.clientX - panMidRef.current.lastX;
          wrap.scrollTop -= e.clientY - panMidRef.current.lastY;
          panMidRef.current.lastX = e.clientX;
          panMidRef.current.lastY = e.clientY;
        }
      }
      if (moveDragRef.current) {
        const node = nodesRef.current.find(n => n.id === moveDragRef.current.nodeId);
        if (node) {
          const { x, y } = eventToCanvasLocal(e);
          node.x = Math.max(0, x - moveDragRef.current.ox);
          node.y = Math.max(0, y - moveDragRef.current.oy);
          const el = document.getElementById('wfe-node-' + moveDragRef.current.nodeId);
          if (el) { el.style.left = node.x + 'px'; el.style.top = node.y + 'px'; }
          drawEdges();
        }
      }
      if (handleDragRef.current) updatePreviewLine(e);
    };
    const onMouseUp = (e) => {
      if (panMidRef.current) {
        panMidRef.current = null;
        wrapRef.current?.classList.remove('pan-mid');
      }
      if (moveDragRef.current) moveDragRef.current = null;
      if (handleDragRef.current) finishHandleDrag(e);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        cancelEdgePopup();
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('keydown', onKeyDown);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawEdges, render]);

  const isTouchDevice =
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
  if (isTouchDevice) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '24px',
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif',
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
        }}
      >
        <div style={{ fontSize: 48, marginBottom: 16 }}>🖥️</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Editor de Fluxo</h2>
        <p style={{ fontSize: 15, color: theme.palette.text.secondary, maxWidth: 320, lineHeight: 1.6, marginBottom: 24 }}>
          Esta ferramenta requer um navegador desktop para funcionar corretamente. Acesse pelo computador para
          editar o fluxo de pedidos.
        </p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            padding: '10px 24px',
            borderRadius: 8,
            border: `1.5px solid ${theme.palette.divider}`,
            background: 'transparent',
            color: theme.palette.primary.main,
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Voltar
        </button>
      </div>
    );
  }

  /* ─── Loading / Error ─────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className={wfeRootClassName(readOnly, isDarkMode)} style={{ alignItems:'center', justifyContent:'center' }}>
        <div style={{ fontSize:14, color: isDarkMode ? '#aab3cc' : '#666' }}>Carregando fluxo…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className={wfeRootClassName(readOnly, isDarkMode)} style={{ alignItems:'center', justifyContent:'center' }}>
        <div style={{ color:'#c62828', fontSize:14 }}>{error}</div>
        {!readOnly && <button className="btn btn-ghost" style={{ marginTop:12 }} onClick={() => navigate(-1)}>← Voltar</button>}
      </div>
    );
  }

  /* ─── Full-screen UI ──────────────────────────────────────────────────── */
  return (
    <div className={wfeRootClassName(readOnly, isDarkMode)}>
      {/* Title bar: breadcrumb + actions */}
      {!readOnly && (
      <div className="wfe-typebar">
        <div className="wfe-typebar-title">
          <span>Fluxo de pedidos</span>
          <span style={{ color:'#ccc' }}>›</span>
          <strong title={typeName}>{typeName}</strong>
        </div>
        <div
          className="wfe-typebar-actions"
          title="Zoom: Ctrl+roda do mouse. Mover a vista: botão do meio arrastando. Rolar: roda sem Ctrl."
        >
          <button type="button" className="btn btn-layout" onClick={autoLayout} title="Reorganizar cards automaticamente">
            ⚡ Auto-layout
          </button>
          <span className="wfe-typebar-sep" aria-hidden />
          <button type="button" className="btn btn-layout" title="Diminuir zoom" onClick={() => setZoomClamped(viewZoomRef.current - 0.12)}>−</button>
          <span className="wfe-zoom-pct">{Math.round(viewZoom * 100)}%</span>
          <button type="button" className="btn btn-layout" title="Aumentar zoom" onClick={() => setZoomClamped(viewZoomRef.current + 0.12)}>+</button>
          <button type="button" className="btn btn-layout" title="Zoom 100%" onClick={() => setZoomClamped(1)}>100%</button>
          <span className="wfe-typebar-sep" aria-hidden />
          <button type="button" className="btn btn-ghost" onClick={() => navigate('/settings/order-workflow/types')}>
            Cancelar
          </button>
          <button type="button" className="btn btn-primary" onClick={saveWorkflow} disabled={saving}>
            {saving ? '⏳ Salvando…' : '💾 Salvar fluxo'}
          </button>
        </div>
      </div>
      )}

      <div className="wfe-layout">
        {/* ── Sidebar ── */}
        {!readOnly && (<aside className="wfe-sidebar">
          <div className="wfe-sidebar-legend">
            <h4>Tipos de seta</h4>
            <div className="wfe-lrow">
              <svg width="40" height="12" viewBox="0 0 46 14"><line x1="2" y1="7" x2="33" y2="7" stroke="#3949ab" strokeWidth="4" strokeLinecap="round"/><polygon points="32,3 43,7 32,11" fill="#3949ab"/></svg>
              <span><b>Principal</b> — 1 por card</span>
            </div>
            <div className="wfe-lrow">
              <svg width="40" height="12" viewBox="0 0 46 14"><line x1="2" y1="7" x2="33" y2="7" stroke="#607d8b" strokeWidth="2" strokeLinecap="round"/><polygon points="32,4 43,7 32,10" fill="#607d8b"/></svg>
              <span><b>Alternativo</b> — desvio</span>
            </div>
            <div className="wfe-lrow">
              <svg width="40" height="12" viewBox="0 0 46 14"><line x1="14" y1="7" x2="33" y2="7" stroke="#f57c00" strokeWidth="2" strokeDasharray="4 2"/><polygon points="6,4 12,7 6,10 0,7" fill="#f57c00"/><polygon points="32,4 43,7 32,10" fill="#f57c00"/></svg>
              <span><b>Decisão</b> — condicional</span>
            </div>
            <div style={{ marginTop:6, paddingTop:6, borderTop:'1px solid #eceff7' }}>
              <div className="wfe-lrow">
                <div style={{ width:22, height:11, border:'2px solid #7c8ff7', borderRadius:3, flexShrink:0, background:'#eef0ff' }}></div>
                <span><b>Inicial</b></span>
              </div>
              <div className="wfe-lrow">
                <div style={{ width:22, height:11, border:'2px dashed #66bb6a', borderRadius:3, flexShrink:0 }}></div>
                <span><b>Final</b></span>
              </div>
              <div className="wfe-lrow" style={{ color:'#9e9e9e', fontStyle:'italic', fontSize:9, marginTop:2, lineHeight:1.25 }}>Seta: passe o mouse e clique para remover</div>
            </div>
          </div>
          <div className="wfe-sidebar-head">
            <h2>Status disponíveis</h2>
            <div className="wfe-sidebar-hint">☝️ Arraste um status para o diagrama para adicioná-lo ao fluxo</div>
          </div>
          <div className="wfe-sidebar-list" ref={sidebarRef}></div>
        </aside>)}

        {/* ── Canvas ── */}
        <div
          className="wfe-canvas-wrap"
          ref={wrapRef}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onDragLeave={onDragLeave}
          onMouseDown={(e) => {
            if (e.button === 1) {
              e.preventDefault();
              panMidRef.current = { lastX: e.clientX, lastY: e.clientY };
              wrapRef.current?.classList.add('pan-mid');
            }
          }}
        >
          <div id="wfe-canvas" ref={canvasRef}>
            {/* SVG layer */}
            <svg id="wfe-svg-layer" ref={svgRef} xmlns="http://www.w3.org/2000/svg">
              <defs>
                <marker id="wfe-m-pri"   markerWidth="12" markerHeight="10" refX="11" refY="5" orient="auto" markerUnits="userSpaceOnUse"><polygon points="0,0 12,5 0,10" fill="#3949ab"/></marker>
                <marker id="wfe-m-pri-h" markerWidth="12" markerHeight="10" refX="11" refY="5" orient="auto" markerUnits="userSpaceOnUse"><polygon points="0,0 12,5 0,10" fill="#e53e3e"/></marker>
                <marker id="wfe-m-alt"   markerWidth="9"  markerHeight="7"  refX="8"  refY="3.5" orient="auto" markerUnits="userSpaceOnUse"><polygon points="0,0 9,3.5 0,7"   fill="#607d8b"/></marker>
                <marker id="wfe-m-alt-h" markerWidth="9"  markerHeight="7"  refX="8"  refY="3.5" orient="auto" markerUnits="userSpaceOnUse"><polygon points="0,0 9,3.5 0,7"   fill="#e53e3e"/></marker>
                <marker id="wfe-m-con-s" markerWidth="14" markerHeight="10" refX="2"  refY="5" orient="auto" markerUnits="userSpaceOnUse"><polygon points="7,0 14,5 7,10 0,5" fill="#f57c00"/></marker>
                <marker id="wfe-m-con-e" markerWidth="9"  markerHeight="7"  refX="8"  refY="3.5" orient="auto" markerUnits="userSpaceOnUse"><polygon points="0,0 9,3.5 0,7"   fill="#f57c00"/></marker>
                <marker id="wfe-m-con-h" markerWidth="9"  markerHeight="7"  refX="8"  refY="3.5" orient="auto" markerUnits="userSpaceOnUse"><polygon points="0,0 9,3.5 0,7"   fill="#e53e3e"/></marker>
                <marker id="wfe-m-prev"  markerWidth="9"  markerHeight="7"  refX="8"  refY="3.5" orient="auto" markerUnits="userSpaceOnUse"><polygon points="0,0 9,3.5 0,7"   fill="#ff9800"/></marker>
              </defs>
              <path id="wfe-preview-line" d="" stroke="#ff9800" strokeWidth="2" fill="none" strokeDasharray="7 4" markerEnd="url(#wfe-m-prev)" style={{ display:'none', pointerEvents:'none' }}/>
            </svg>

            {/* Empty state */}
            <div className="wfe-empty" ref={emptyRef} style={{ display:'none' }}>
              <svg width="80" height="60" viewBox="0 0 80 60" fill="none">
                <rect x="2" y="16" width="30" height="26" rx="5" fill="#9fa8da"/>
                <rect x="48" y="16" width="30" height="26" rx="5" fill="#9fa8da"/>
                <line x1="32" y1="29" x2="47" y2="29" stroke="#9fa8da" strokeWidth="3"/>
                <polygon points="45,26 52,29 45,32" fill="#9fa8da"/>
              </svg>
              <p>Arraste um status aqui</p>
              <small>Os status vêm da barra esquerda</small>
            </div>
          </div>
        </div>
      </div>

      {/* ── Node modal ── */}
      {!readOnly && <div ref={modalRef}>
        <div className="wfe-backdrop" onClick={e => { if (e.target.classList.contains('wfe-backdrop')) closeModal(); }}>
          <div className="wfe-modal">
            <h3>✏️ Editar status</h3>
            <div className="wfe-fld">
              <label>Nome</label>
              <input type="text" id="wfe-m-name"/>
            </div>
            <div className="wfe-fld">
              <label>Cor</label>
              <div className="wfe-color-row">
                <input type="color" id="wfe-m-color" onInput={syncColorPreview}/>
                <div className="wfe-color-preview" id="wfe-m-preview">prévia</div>
              </div>
            </div>
            <div className="wfe-fld" style={{ marginTop:14 }}>
              <label>Comportamento</label>
              <div className="wfe-toggle-row">
                <div className="wfe-toggle-info">
                  <p>📋 Exibir Formulário de Customização</p>
                  <small>Abre o formulário ao entrar neste status</small>
                </div>
                <label className="wfe-toggle">
                  <input type="checkbox" id="wfe-m-form"/>
                  <span className="wfe-slider"></span>
                </label>
              </div>
            </div>
            <div id="wfe-m-derived-info" style={{ marginTop:12, padding:'10px 12px', borderRadius:8, background:'#f7f8fc', fontSize:11, color:'#7a7f9a', display:'none' }}>
              <span id="wfe-m-derived-text"></span>
            </div>
            <div className="wfe-modal-actions">
              <button className="btn btn-danger" onClick={removeFromFlow}>Remover do fluxo</button>
              <button className="btn btn-ghost" style={{ borderColor:'#e4e6ef', color:'#666' }} onClick={closeModal}>Cancelar</button>
              <button className="btn btn-primary" onClick={saveNodeModal}>Salvar</button>
            </div>
          </div>
        </div>
      </div>}

      {/* ── Edge type popup ── */}
      {!readOnly && <div className="wfe-edge-popup" ref={popupRef}>
        <h4>Qual tipo de seta?</h4>
        <div className="sub" id="wfe-ep-sub">Escolha como esta transição se comporta no fluxo</div>
        <div className="wfe-type-cards">
          <div className="wfe-type-card" id="wfe-tc-primary" onClick={() => chooseEdgeType('primary')}>
            <svg className="tc-icon" width="46" height="14">
              <line x1="2" y1="7" x2="33" y2="7" stroke="#3949ab" strokeWidth="4" strokeLinecap="round"/>
              <polygon points="32,3 43,7 32,11" fill="#3949ab"/>
            </svg>
            <div className="tc-info">
              <strong>Principal</strong>
              <small id="wfe-tc-primary-hint">Caminho padrão — 1 por card</small>
            </div>
          </div>
          <div className="wfe-type-card" id="wfe-tc-alternative" onClick={() => chooseEdgeType('alternative')}>
            <svg className="tc-icon" width="46" height="14">
              <line x1="2" y1="7" x2="33" y2="7" stroke="#607d8b" strokeWidth="2" strokeLinecap="round"/>
              <polygon points="32,4 43,7 32,10" fill="#607d8b"/>
            </svg>
            <div className="tc-info">
              <strong>Alternativo</strong>
              <small>Desvio possível do fluxo</small>
            </div>
          </div>
          <div className="wfe-type-card" id="wfe-tc-conditional" onClick={() => chooseEdgeType('conditional')}>
            <svg className="tc-icon" width="46" height="14">
              <line x1="14" y1="7" x2="33" y2="7" stroke="#f57c00" strokeWidth="2" strokeDasharray="4 2"/>
              <polygon points="6,4 12,7 6,10 0,7" fill="#f57c00"/>
              <polygon points="32,4 43,7 32,10" fill="#f57c00"/>
            </svg>
            <div className="tc-info">
              <strong>Decisão</strong>
              <small>Transição condicional (com regra)</small>
            </div>
          </div>
        </div>
        <div className="wfe-cond-label-area">
          <div style={{ fontSize:11, color:'#9e9e9e', margin:'10px 0 6px' }}>Descreva a condição:</div>
          <input type="text" id="wfe-cond-input" placeholder="Ex: se formulário foi enviado…"
            onKeyDown={e => { if (e.key==='Enter') confirmConditional(); if (e.key==='Escape') { e.stopPropagation(); cancelEdgePopup(); } }}
          />
          <div className="wfe-popup-actions">
            <button className="btn btn-ghost" style={{ flex:1, fontSize:12, borderColor:'#e4e6ef', color:'#666' }} onClick={cancelEdgePopup}>Cancelar</button>
            <button className="btn btn-primary" style={{ flex:1, fontSize:12 }} onClick={confirmConditional}>Criar seta</button>
          </div>
        </div>
        <div className="wfe-popup-actions" id="wfe-ep-cancel-row">
          <button className="btn btn-ghost" style={{ fontSize:12, borderColor:'#e4e6ef', color:'#aaa' }} onClick={cancelEdgePopup}>Cancelar</button>
        </div>
      </div>}

      {/* Toast */}
      <div className="wfe-toast" ref={toastRef}></div>
    </div>
  );
}
